import React, { useEffect } from "react";
import { JOURNEY, SARKAR_PHOTOS } from "../lib/content";
import MihrabFrame from "../components/site/MihrabFrame";
import GeometricDivider from "../components/site/GeometricDivider";
import SectionTitle from "../components/site/SectionTitle";
import { Quote } from "lucide-react";

const HeaderSection = () => (
  <section className="pattern-emerald text-ivory pt-32 pb-24" data-testid="about-hero">
    <div className="container-page text-center">
      <span className="text-[11px] uppercase tracking-[0.32em] text-gold">About</span>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Hazrat Shahanshah Sufi Sarkar</h1>
      <p className="calligraphy text-gold text-xl mt-5">Habibul Mahboob · Shahanshah-e-Qutub</p>
    </div>
  </section>
);

const Biography = () => (
  <section className="section-pad" data-testid="about-biography">
    <div className="container-page grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5 reveal">
        <MihrabFrame
          src="/portraits/sarkar_about.png"
          backdrop="https://static.prod-images.emergentagent.com/jobs/94b9a04e-65da-4587-aedc-289bb56315d5/images/a31ce9fc6e466ce244c31ca162c89392696a9bdcdeba64e2f8e20f34d60e9f0d.png"
          alt="Portrait of Hazrat Shahanshah Sufi Sarkar"
          label="ABOUT_PORTRAIT"
          aspect="aspect-[3/4]"
        />
      </div>
      <div className="md:col-span-7 reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Biography</span>
        <h2 className="font-serif text-3xl md:text-4xl text-emerald mt-4 leading-tight">A revered Indian Sufi saint and Shahanshah-e-Qutub</h2>
        <p className="text-charcoal-muted leading-relaxed mt-6">
          Born on <strong className="text-emerald">10 Rabi'l, 1397 AH</strong>, embodying divine blessings bestowed by the mercy of Rasool Allah ﷺ. Venerated by majestic titles signifying his role as a divine revealer of truths and supreme bestower of spiritual and worldly blessings.
        </p>
        <blockquote className="mt-8 border-l-2 border-gold pl-6 italic font-serif text-xl text-emerald">
          HAZRAT ABU MUHAMMAD BADE PEER MUHIYYUL SHAH NAWAZ ALLAH QADIR GHOUS-E-RABBANI
        </blockquote>
      </div>
    </div>
  </section>
);

const Journey = () => (
  <section className="pattern-bg section-pad" data-testid="about-journey">
    <div className="container-page">
      <SectionTitle eyebrow="Spiritual Journey" title="From a Mother's Prayer to the King of Spiritual Poverty" />
      <GeometricDivider />
      <div className="relative">
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gold/40 -translate-x-1/2" aria-hidden />
        <div className="space-y-12">
          {JOURNEY.map((j, i) => (
            <div key={j.title} className={`relative grid md:grid-cols-2 gap-8 reveal ${i % 2 === 0 ? "" : "md:[&>div:first-child]:order-2"}`}>
              <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                <span className="text-[11px] uppercase tracking-[0.32em] text-gold">{j.year}</span>
                <h3 className="font-serif text-2xl text-emerald mt-2">{j.title}</h3>
                <p className="text-charcoal-muted mt-3 leading-relaxed">{j.desc}</p>
              </div>
              <div className="hidden md:block" />
              <span className="absolute left-4 md:left-1/2 top-2 -translate-x-1/2 grid place-items-center h-8 w-8 rounded-full bg-ivory border border-gold">
                <span className="h-2 w-2 rounded-full bg-gold" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PullQuote = ({ children }) => (
  <blockquote className="my-12 mx-auto max-w-3xl text-center font-serif text-2xl md:text-3xl text-emerald reveal">
    <Quote className="text-gold mx-auto mb-4" size={28} />
    "{children}"
  </blockquote>
);

const Teachings = () => (
  <section className="section-pad" data-testid="about-teachings">
    <div className="container-page max-w-4xl">
      <SectionTitle eyebrow="Teachings & Philosophy" title="The Sultan-Ul-Faqr" align="center" />
      <p className="text-charcoal-muted text-lg leading-relaxed reveal">
        His teachings centered on love, compassion, and self-realization — the highest principles of Faqr. <span className="text-emerald font-semibold">"Sultan-Ul-Faqr"</span> means the <em>King of Spiritual Poverty</em>: not material lack, but the pinnacle of spiritual richness — a heart utterly free of worldly attachment and filled with the love and presence of the Divine.
      </p>
      <PullQuote>Enlightenment lies in this deep, exclusive connection with Allah and the unity of all creation.</PullQuote>
    </div>
  </section>
);

const Miracles = () => (
  <section className="pattern-emerald text-ivory section-pad" data-testid="about-miracles">
    <div className="container-page grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Miracles & Barakah</span>
        <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">The Flow of Divine Blessings</h2>
        <p className="mt-6 text-ivory/80 leading-relaxed">
          Revered for divine miracles (karamat). Many devotees experienced miraculous healings and answered prayers through his grace. His authority as a bestower of barakah brings solace, guidance, and sustenance to those in need.
        </p>
      </div>
      <p className="calligraphy text-gold text-2xl md:text-3xl leading-relaxed reveal">
        "Inke hukum se barkat aa sakte hai" — through his command, blessings can flow.
      </p>
    </div>
  </section>
);

const Humanitarian = () => (
  <section className="section-pad" data-testid="about-humanitarian">
    <div className="container-page grid md:grid-cols-2 gap-12">
      <div className="reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Humanitarian Work</span>
        <h2 className="font-serif text-3xl md:text-4xl text-emerald mt-4">Charitable Initiatives</h2>
        <p className="text-charcoal-muted mt-6 leading-relaxed">
          Founder of the Hazrat Shahanshah Sufi Sarkar Charitable Trust, which runs humanitarian and social-welfare programs to uplift underprivileged communities — providing education, healthcare, and support to the needy with food, clothing, and shelter.
        </p>
      </div>
      <div className="reveal">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Legacy</span>
        <h2 className="font-serif text-3xl md:text-4xl text-emerald mt-4">Spiritual Influence</h2>
        <p className="text-charcoal-muted mt-6 leading-relaxed">
          His spirituality continues to inspire seekers across backgrounds. His followers, the murids, carry forward his message of love, tolerance, and unity — across cities, countries, and generations.
        </p>
      </div>
    </div>
  </section>
);

export default function About() {
  useEffect(() => { document.title = "About — Hazrat Shahanshah Sufi Sarkar"; }, []);
  return (
    <div data-testid="about-page">
      <HeaderSection />
      <Biography />
      <Journey />
      <Teachings />
      <Miracles />
      <Humanitarian />
    </div>
  );
}
